import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Plus, XCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import {
  checkRequisite,
  isFarmFormValid,
  sanitizeFarmPayload,
  validateFarmForm,
  type FarmFormErrors,
  type RequisiteField,
} from '../structureLogic';
import type { ClusterOut, FarmIn, FarmWithAggregate } from '../types';
import { useCreateFarm, useUpdateFarm } from '../hooks/useStructureQueries';

const NO_CLUSTER = '__none__';

interface FarmDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  clusters: ClusterOut[];
  /** Если задан — режим редактирования; иначе создание. */
  farm?: FarmWithAggregate | null;
  /** Предвыбранный кластер при создании (из контекста строки дерева). */
  defaultClusterId?: string | null;
  /**
   * AK-584 — «＋ Добавить поле» из карточки хозяйства. Только в режиме
   * редактирования: у ещё не созданного хозяйства поля быть не может.
   */
  onAddField?: (farmId: string) => void;
}

interface FormState {
  name: string;
  short_name: string;
  cluster_id: string | null;
  has_own_legal_entity: boolean;
  legal_entity_name: string;
  inn: string;
  ogrn: string;
  kpp: string;
  legal_address: string;
  actual_address: string;
  director_name: string;
  director_phone: string;
  director_email: string;
}

function emptyState(defaultClusterId?: string | null): FormState {
  return {
    name: '',
    short_name: '',
    cluster_id: defaultClusterId ?? null,
    has_own_legal_entity: false,
    legal_entity_name: '',
    inn: '',
    ogrn: '',
    kpp: '',
    legal_address: '',
    actual_address: '',
    director_name: '',
    director_phone: '',
    director_email: '',
  };
}

function fromFarm(f: FarmWithAggregate): FormState {
  return {
    name: f.name,
    short_name: f.short_name ?? '',
    cluster_id: f.cluster_id ?? null,
    has_own_legal_entity: f.has_own_legal_entity,
    legal_entity_name: f.legal_entity_name ?? '',
    inn: f.inn ?? '',
    ogrn: f.ogrn ?? '',
    kpp: f.kpp ?? '',
    legal_address: f.legal_address ?? '',
    actual_address: f.actual_address ?? '',
    director_name: f.director_name ?? '',
    director_phone: f.director_phone ?? '',
    director_email: f.director_email ?? '',
  };
}

/** Инлайн-подсказка под полем реквизита: зелёная (ok) / красная (ошибка). */
function RequisiteHint({ field, value }: { field: RequisiteField; value: string }) {
  const check = checkRequisite(field, value);
  if (check.ok === null) return null;
  return (
    <p
      className={
        check.ok
          ? 'flex items-center gap-1 text-xs text-primary'
          : 'flex items-center gap-1 text-xs text-destructive'
      }
    >
      {check.ok ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <XCircle className="h-3 w-3" />
      )}
      {check.message}
    </p>
  );
}

/**
 * FarmDrawer — создание / редактирование хозяйства (FarmIn целиком).
 *
 * Тумблер «Своё юрлицо» раскрывает секцию реквизитов с инлайн-валидацией
 * ИНН (10/12) / ОГРН (13/15) / КПП (9) — зеркало pattern из FarmIn. При
 * выключенном тумблере реквизиты не обязательны и обнуляются перед отправкой
 * (sanitizeFarmPayload). Финальная проверка — на бэке (422 по pattern).
 */
export function FarmDrawer({
  open,
  onOpenChange,
  companyId,
  clusters,
  farm,
  defaultClusterId,
  onAddField,
}: FarmDrawerProps) {
  const isEdit = !!farm;
  const create = useCreateFarm();
  const update = useUpdateFarm();
  const [form, setForm] = useState<FormState>(() => emptyState(defaultClusterId));
  const [errors, setErrors] = useState<FarmFormErrors>({});
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setForm(farm ? fromFarm(farm) : emptyState(defaultClusterId));
      setErrors({});
      setSubmitError(undefined);
    }
  }, [open, farm, defaultClusterId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const pending = create.isPending || update.isPending;

  const submit = async () => {
    const validation = validateFarmForm({
      name: form.name,
      cluster_id: form.cluster_id,
      has_own_legal_entity: form.has_own_legal_entity,
      inn: form.inn,
      ogrn: form.ogrn,
      kpp: form.kpp,
      director_email: form.director_email,
    });
    setErrors(validation);
    if (!isFarmFormValid(validation)) return;

    const payload = sanitizeFarmPayload({
      company_id: companyId,
      cluster_id: form.cluster_id,
      name: form.name.trim(),
      short_name: form.short_name,
      has_own_legal_entity: form.has_own_legal_entity,
      legal_entity_name: form.legal_entity_name,
      inn: form.inn,
      ogrn: form.ogrn,
      kpp: form.kpp,
      legal_address: form.legal_address,
      actual_address: form.actual_address,
      director_name: form.director_name,
      director_phone: form.director_phone,
      director_email: form.director_email,
    }) as unknown as FarmIn;

    try {
      if (isEdit && farm) {
        await update.mutateAsync({ farmId: farm.id, payload });
        toast.success(`Хозяйство «${payload.name}» сохранено`);
      } else {
        await create.mutateAsync(payload);
        toast.success(`Хозяйство «${payload.name}» создано`);
      }
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setSubmitError(e.detail);
      else setSubmitError('Не удалось сохранить хозяйство');
    }
  };

  const clusterValue = useMemo(
    () => form.cluster_id ?? NO_CLUSTER,
    [form.cluster_id],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[520px]">
        <SheetTitle>{isEdit ? 'Редактировать хозяйство' : 'Новое хозяйство'}</SheetTitle>
        <SheetDescription>
          {isEdit
            ? 'Измените данные хозяйства. Обязательно только название.'
            : 'Добавим хозяйство в структуру. Обязательно только название — остальное можно заполнить позже.'}
        </SheetDescription>

        {/* AK-584: главный вход в добавление поля из карточки хозяйства —
            над формой реквизитов, потому что это то, ради чего хозяйство и
            заводили, а реквизиты можно заполнить когда-нибудь потом. */}
        {isEdit && farm && onAddField && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-secondary/30 px-3 py-3">
            <div className="min-w-0">
              <div className="text-sm font-medium">
                {farm.field_count === 0 ? 'Полей пока нет' : `Полей: ${farm.field_count}`}
              </div>
              <div className="text-xs text-muted-foreground">
                Поле можно обвести на карте или загрузить файлом
              </div>
            </div>
            <Button
              size="sm"
              data-testid="farm-drawer-add-field"
              onClick={() => {
                onOpenChange(false);
                onAddField(farm.id);
              }}
            >
              <Plus className="h-4 w-4" />
              Добавить поле
            </Button>
          </div>
        )}

        <div className="mt-6 space-y-5">
          {/* Название + короткое имя + кластер */}
          <div className="space-y-1.5">
            <Label htmlFor="farm-name">
              Название <span className="text-destructive">*</span>
            </Label>
            <Input
              id="farm-name"
              placeholder="напр. «Заря-3»"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="farm-short">Короткое имя</Label>
              <Input
                id="farm-short"
                placeholder="для списков"
                value={form.short_name}
                onChange={(e) => set('short_name', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Кластер</Label>
              <Select
                value={clusterValue}
                onValueChange={(v) => set('cluster_id', v === NO_CLUSTER ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Вне кластера" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CLUSTER}>Вне кластера</SelectItem>
                  {clusters.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            Можно оставить «Вне кластера» — привяжете позже через «Переместить в кластер».
          </p>

          {/* Тумблер «Своё юрлицо» */}
          <div className="flex items-center gap-3 rounded-md border border-border bg-secondary/30 px-3 py-3">
            <Switch
              checked={form.has_own_legal_entity}
              onCheckedChange={(v) => set('has_own_legal_entity', v)}
              aria-label="Своё юрлицо"
            />
            <div className="min-w-0">
              <div className="text-sm font-medium">Своё юрлицо</div>
              <div className="text-xs text-muted-foreground">
                Заполнить ИНН, ОГРН, реквизиты и адреса
              </div>
            </div>
          </div>

          {/* Реквизиты юрлица */}
          {form.has_own_legal_entity && (
            <fieldset className="space-y-4 rounded-md border border-border bg-secondary/20 p-4">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-accent-brand">
                Реквизиты юрлица
              </legend>

              <div className="space-y-1.5">
                <Label htmlFor="farm-legal-name">Наименование юрлица</Label>
                <Input
                  id="farm-legal-name"
                  placeholder="ООО «Заря»"
                  value={form.legal_entity_name}
                  onChange={(e) => set('legal_entity_name', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="farm-inn">ИНН</Label>
                  <Input
                    id="farm-inn"
                    inputMode="numeric"
                    placeholder="7701234567"
                    value={form.inn}
                    onChange={(e) => set('inn', e.target.value)}
                    aria-invalid={!!errors.inn}
                  />
                  <RequisiteHint field="inn" value={form.inn} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="farm-kpp">КПП</Label>
                  <Input
                    id="farm-kpp"
                    inputMode="numeric"
                    placeholder="770101001"
                    value={form.kpp}
                    onChange={(e) => set('kpp', e.target.value)}
                    aria-invalid={!!errors.kpp}
                  />
                  <RequisiteHint field="kpp" value={form.kpp} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="farm-ogrn">ОГРН</Label>
                <Input
                  id="farm-ogrn"
                  inputMode="numeric"
                  placeholder="1027700123456"
                  value={form.ogrn}
                  onChange={(e) => set('ogrn', e.target.value)}
                  aria-invalid={!!errors.ogrn}
                />
                <RequisiteHint field="ogrn" value={form.ogrn} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="farm-legal-addr">Юридический адрес</Label>
                <Input
                  id="farm-legal-addr"
                  placeholder="Индекс, регион, город, улица, дом"
                  value={form.legal_address}
                  onChange={(e) => set('legal_address', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="farm-actual-addr">
                  Фактический адрес{' '}
                  <span className="font-normal text-muted-foreground">· если отличается</span>
                </Label>
                <Input
                  id="farm-actual-addr"
                  placeholder="— совпадает с юридическим"
                  value={form.actual_address}
                  onChange={(e) => set('actual_address', e.target.value)}
                />
              </div>
            </fieldset>
          )}

          {/* Руководитель */}
          <fieldset className="space-y-4 rounded-md border border-border bg-secondary/20 p-4">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Руководитель · необязательно
            </legend>
            <div className="space-y-1.5">
              <Label htmlFor="farm-dir">ФИО директора</Label>
              <Input
                id="farm-dir"
                placeholder="Иванов Иван Иванович"
                value={form.director_name}
                onChange={(e) => set('director_name', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="farm-phone">Телефон</Label>
                <Input
                  id="farm-phone"
                  inputMode="tel"
                  placeholder="+7 ___ ___-__-__"
                  value={form.director_phone}
                  onChange={(e) => set('director_phone', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="farm-email">Email</Label>
                <Input
                  id="farm-email"
                  type="email"
                  placeholder="director@zarya.ru"
                  value={form.director_email}
                  onChange={(e) => set('director_email', e.target.value)}
                  aria-invalid={!!errors.director_email}
                />
                {errors.director_email && (
                  <p className="text-xs text-destructive">{errors.director_email}</p>
                )}
              </div>
            </div>
          </fieldset>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </div>

        <div className="mt-6 flex gap-3 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Отмена
          </Button>
          <Button
            className="flex-1"
            onClick={submit}
            disabled={pending}
          >
            {pending
              ? 'Сохраняем…'
              : isEdit
                ? 'Сохранить'
                : 'Создать хозяйство'}
          </Button>
        </div>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Реквизиты нужны для документов и отчётности. Без своего юрлица — секция скрыта.
        </p>
      </SheetContent>
    </Sheet>
  );
}
